import { useEffect, useState, type ReactNode } from 'react';
import { Badge, Button, DataTable, Input, Skeleton, Toastify } from 'lift-ui';
import type { BadgeProps, TableColumn } from 'lift-ui';
import { jobsErrorMessage } from '../api';
import { useSyncRun, useSyncRunZip, useSyncRuns } from '../hooks';
import type { SyncRunItem, SyncRunSummary, ZipStatus } from '../types';
import { formatCount, formatTimestamp } from '../utils';

const PAGE_SIZE = 25;

type Tone = { label: string; variant: BadgeProps['variant'] };

const AUDIENCE: Record<SyncRunSummary['audience'], string> = {
  admin: 'GV admins',
  brand_admin: 'Brand admins',
  creator: 'Creators',
};

const MODE: Record<SyncRunSummary['mode'], string> = {
  database_only: 'Database only',
  live: 'Live',
};

const INTENT: Record<SyncRunSummary['intent'], string> = {
  sync_all: 'Sync all',
  retry_failed: 'Retry failed',
  resend_invites: 'Resend invites',
};

const RUN_STATUS: Record<SyncRunSummary['status'], Tone> = {
  preprocessing: { label: 'Checking', variant: 'blueLight' },
  queued: { label: 'Queued', variant: 'blueLight' },
  running: { label: 'Running', variant: 'blue' },
  completed: { label: 'Completed', variant: 'success' },
  completed_with_errors: { label: 'Completed with errors', variant: 'warning' },
  failed: { label: 'Failed', variant: 'danger' },
};

// `sent` is the legacy email-era status: no zip was stored, so it reads as unavailable.
// `not_applicable` has nothing to report, so it renders no badge.
const ZIP: Partial<Record<ZipStatus, Tone>> = {
  ready: { label: 'Zip ready', variant: 'success' },
  pending: { label: 'Zip pending', variant: 'blueLight' },
  failed: { label: 'Zip failed', variant: 'danger' },
  unavailable: { label: 'Zip unavailable', variant: 'greyLight' },
  sent: { label: 'Zip unavailable', variant: 'greyLight' },
};

const ITEM_STATUS: Record<string, Tone> = {
  queued: { label: 'Queued', variant: 'blueLight' },
  running: { label: 'Running', variant: 'blue' },
  retrying: { label: 'Retrying', variant: 'blue' },
  succeeded: { label: 'Succeeded', variant: 'success' },
  skipped: { label: 'Skipped', variant: 'warning' },
  failed: { label: 'Failed', variant: 'danger' },
  partial: { label: 'Partial', variant: 'warning' },
};

const ITEM_FILTERS: { status: string; label: string; count: (run: SyncRunSummary) => number }[] = [
  { status: '', label: 'All', count: (run) => run.total_count },
  { status: 'queued', label: 'Queued', count: (run) => run.pending_count },
  { status: 'running', label: 'Running', count: (run) => run.running_count },
  { status: 'succeeded', label: 'Succeeded', count: (run) => run.succeeded_count },
  { status: 'skipped', label: 'Skipped', count: (run) => run.skipped_count },
  { status: 'failed', label: 'Failed', count: (run) => run.failed_count },
  { status: 'partial', label: 'Partial', count: (run) => run.partial_count },
];

const finishedCount = (run: SyncRunSummary) => run.total_count - run.pending_count - run.running_count;

const progressLabel = (run: SyncRunSummary) => {
  if (run.status === 'preprocessing') return 'Checking';
  return run.total_count === 0
    ? 'Queueing'
    : `${formatCount(finishedCount(run))} of ${formatCount(run.total_count)}`;
};

const StatusBadge = ({ tone }: { tone: Tone }) => <Badge variant={tone.variant}>{tone.label}</Badge>;

const ProgressBar = ({ run, className = 'h-1.5' }: { run: SyncRunSummary; className?: string }) => {
  const total = Math.max(run.total_count, 1);
  const segments = [
    { key: 'succeeded', value: run.succeeded_count, className: 'bg-emerald-500' },
    { key: 'skipped', value: run.skipped_count, className: 'bg-amber-400' },
    { key: 'failed', value: run.failed_count, className: 'bg-destructive' },
  ];

  return (
    <div
      className={`flex w-full overflow-hidden rounded-full bg-muted ${className}`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={run.total_count}
      aria-valuenow={finishedCount(run)}
      aria-label={`${AUDIENCE[run.audience]} progress`}
    >
      {segments.map((segment) =>
        segment.value > 0 ? (
          <span
            key={segment.key}
            className={`h-full transition-[width] duration-500 ease-out ${segment.className}`}
            style={{ width: `${(segment.value / total) * 100}%` }}
          />
        ) : null
      )}
    </div>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex min-w-0 flex-col gap-1">
    <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
    <div className="truncate text-sm">{children}</div>
  </div>
);

const Count = ({ label, value, dot }: { label: string; value: number; dot: string }) => (
  <div className="flex items-center gap-2 text-sm">
    <span className={`size-2 rounded-full ${dot}`} aria-hidden />
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium tabular-nums">{formatCount(value)}</span>
  </div>
);

interface RunListProps {
  runs: SyncRunSummary[];
  activeId: string | null;
  loading: boolean;
  onSelect: (id: string) => void;
}

const RunList = ({ runs, activeId, loading, onSelect }: RunListProps) => {
  if (loading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-[88px] w-full" />
        ))}
      </div>
    );
  }

  if (runs.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border p-6 text-sm text-muted-foreground">
        No sync runs yet. Start one from the Sync to LIFT tab.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-2" aria-label="Sync runs">
      {runs.map((run) => {
        const active = run.id === activeId;
        return (
          <li key={run.id}>
            <button
              type="button"
              aria-current={active ? 'true' : undefined}
              onClick={() => onSelect(run.id)}
              className={`flex w-full flex-col gap-2 rounded-md border px-4 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                active ? 'border-foreground/30 bg-muted/60' : 'border-border hover:bg-muted/40'
              }`}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">{AUDIENCE[run.audience]}</span>
                <StatusBadge tone={RUN_STATUS[run.status]} />
              </span>
              <ProgressBar run={run} />
              <span className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span>{formatTimestamp(run.created_at)}</span>
                <span className="tabular-nums">{progressLabel(run)}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

interface SyncRunsTabProps {
  enabled: boolean;
  onSelectJob: (jobId: string) => void;
  initialRunId?: string | null;
}

export const SyncRunsTab = ({ enabled, onSelectJob, initialRunId = null }: SyncRunsTabProps) => {
  const runs = useSyncRuns(enabled);
  const [selectedId, setSelectedId] = useState<string | null>(initialRunId);
  const [page, setPage] = useState(1);
  const [itemStatus, setItemStatus] = useState('');
  const [emailQuery, setEmailQuery] = useState('');
  const zip = useSyncRunZip();

  // A new run opened from the Sync to LIFT tab arrives as a changed prop.
  useEffect(() => {
    if (!initialRunId) return;
    setSelectedId(initialRunId);
    setPage(1);
    setItemStatus('');
    setEmailQuery('');
  }, [initialRunId]);

  const downloadZip = (id: string) =>
    zip.mutate(id, {
      onSuccess: ({ url }) => window.location.assign(url),
      onError: (error) => {
        const status = (error as { response?: { status?: number } })?.response?.status;
        Toastify(
          'error',
          status === 404
            ? 'The zip is not ready yet.'
            : jobsErrorMessage(error, 'Could not download the zip.')
        );
      },
    });

  const runList = runs.data?.data ?? [];
  const activeId = selectedId ?? runList[0]?.id ?? null;
  const detail = useSyncRun(activeId, {
    page,
    pageSize: PAGE_SIZE,
    status: itemStatus || undefined,
    q: emailQuery.trim() || undefined,
  });

  const openRun = (id: string) => {
    setSelectedId(id);
    setPage(1);
    setItemStatus('');
    setEmailQuery('');
  };

  const itemColumns: TableColumn<SyncRunItem>[] = [
    {
      key: 'email',
      header: 'Email',
      accessor: 'email',
      render: (_value, row) => <span className="break-all">{row.email}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      accessor: 'status',
      render: (_value, row) => (
        <StatusBadge tone={ITEM_STATUS[row.status] ?? { label: row.status, variant: 'greyLight' }} />
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      accessor: 'reason',
      render: (_value, row) =>
        row.reason ? <span className="text-sm">{row.reason}</span> : <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'job_id',
      header: '',
      accessor: 'job_id',
      align: 'right',
      render: (_value, row) =>
        row.job_id ? (
          <Button type="button" size="sm" variant="ghost" onClick={() => onSelectJob(row.job_id as string)}>
            View job
          </Button>
        ) : null,
    },
  ];

  const run = detail.data;
  const totalItems = run?.items_pagination?.total_count ?? run?.items.length ?? 0;
  const totalPages = run?.items_pagination?.total_pages || 1;

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(260px,320px)_minmax(0,1fr)]">
      <RunList runs={runList} activeId={activeId} loading={runs.isLoading} onSelect={openRun} />

      <section className="flex min-w-0 flex-col gap-6 rounded-md border border-border p-5" aria-live="polite">
        {!activeId && !runs.isLoading && (
          <p className="text-sm text-muted-foreground">Select a sync run to see who was synced.</p>
        )}

        {activeId && detail.isError && (
          <p className="text-sm text-destructive">
            {jobsErrorMessage(detail.error, 'This sync run could not be loaded.')}
          </p>
        )}

        {activeId && !run && detail.isLoading && (
          <div className="flex flex-col gap-4">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-2 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        )}

        {run && (
          <>
            <header className="flex flex-col gap-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex flex-col gap-1">
                  <h2 className="text-lg font-semibold">{AUDIENCE[run.audience]}</h2>
                  <p className="text-sm text-muted-foreground">
                    Started {formatTimestamp(run.created_at)} · {MODE[run.mode]} · {INTENT[run.intent]}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge tone={RUN_STATUS[run.status]} />
                  {ZIP[run.zip_status] && <StatusBadge tone={ZIP[run.zip_status] as Tone} />}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!run.zip_ready || zip.isPending}
                    onClick={() => downloadZip(run.id)}
                  >
                    {zip.isPending ? 'Working…' : 'Download zip'}
                  </Button>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-2xl font-semibold tabular-nums">{progressLabel(run)}</span>
                  <span className="text-sm text-muted-foreground">processed</span>
                </div>
                <ProgressBar run={run} className="h-2" />
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  <Count label="Succeeded" value={run.succeeded_count} dot="bg-emerald-500" />
                  <Count label="Skipped" value={run.skipped_count} dot="bg-amber-400" />
                  <Count label="Failed" value={run.failed_count} dot="bg-destructive" />
                  <Count label="Partial" value={run.partial_count} dot="bg-amber-600" />
                  <Count label="Waiting" value={run.pending_count + run.running_count} dot="bg-muted-foreground/40" />
                </div>
              </div>

              <div className="grid gap-4 border-t border-border pt-4 sm:grid-cols-2">
                <Field label="Requester">{run.requested_by_email ?? '—'}</Field>
                <Field label="Updated">{formatTimestamp(run.updated_at)}</Field>
              </div>
            </header>

            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex flex-wrap gap-1" role="group" aria-label="Filter people by status">
                  {ITEM_FILTERS.map((filter) => (
                    <Button
                      key={filter.label}
                      size="sm"
                      variant={itemStatus === filter.status ? 'default' : 'ghost'}
                      aria-pressed={itemStatus === filter.status}
                      onClick={() => {
                        setItemStatus(filter.status);
                        setPage(1);
                      }}
                    >
                      {filter.label}
                      <span className="ml-1.5 tabular-nums opacity-70">{formatCount(filter.count(run))}</span>
                    </Button>
                  ))}
                </div>
                <Input
                  className="w-full sm:ml-auto sm:w-64"
                  placeholder="Search email"
                  aria-label="Search email"
                  value={emailQuery}
                  onChange={(event) => {
                    setEmailQuery(event.target.value);
                    setPage(1);
                  }}
                />
              </div>

              <DataTable<SyncRunItem>
                data={run.items}
                columns={itemColumns}
                loading={false}
                emptyMessage="No people match this filter"
                stickyHeader
                maxHeight="560px"
                pagination={{
                  currentPage: page,
                  totalPages,
                  pageSize: PAGE_SIZE,
                  totalItems,
                  onPageChange: setPage,
                  onPageSizeChange: () => undefined,
                  pageSizeOptions: [PAGE_SIZE],
                }}
              />
            </div>
          </>
        )}
      </section>
    </div>
  );
};
